package com.bikompanion.data.analytics.web

import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject

/** GA4 rejects a whole request carrying more events than this. */
internal const val MaxEventsPerBatch = 25

/**
 * Renders queued events as Measurement Protocol request bodies for `/api/analytics`, one per
 * batch of [MaxEventsPerBatch].
 *
 * The measurement id and API secret are absent on purpose: the edge function adds them, so
 * neither ships in the bundle.
 */
internal fun webAnalyticsPayloads(
    records: List<WebAnalyticsRecord>,
    defaultParameters: Map<String, Any> = emptyMap(),
    userProperties: Map<String, String> = emptyMap(),
): List<String> =
    records
        .chunked(MaxEventsPerBatch)
        .map { batch -> batch.toPayload(defaultParameters, userProperties).toString() }

private fun List<WebAnalyticsRecord>.toPayload(
    defaultParameters: Map<String, Any>,
    userProperties: Map<String, String>,
): JsonObject =
    buildJsonObject {
        put("client_id", first().context.clientId)
        if (userProperties.isNotEmpty()) {
            putJsonObject("user_properties") {
                userProperties.forEach { (name, value) -> putJsonObject(name) { put("value", value) } }
            }
        }
        put("events", buildJsonArray { this@toPayload.forEach { add(it.toEvent(defaultParameters)) } })
    }

private fun WebAnalyticsRecord.toEvent(defaultParameters: Map<String, Any>): JsonObject =
    buildJsonObject {
        put("name", event.name)
        put("timestamp_micros", timestampMicros)
        putJsonObject("params") {
            defaultParameters.forEach { (name, value) -> put(name, value.toJsonPrimitive()) }
            pageParameters.forEach { (name, value) -> put(name, value) }
            event.parameters.forEach { (name, value) -> put(name, value.toJsonPrimitive()) }
            // Documented as strings, and both mandatory: without them GA4 keeps the event but
            // shows it in no standard report.
            put("session_id", context.sessionId)
            put("engagement_time_msec", context.engagementTimeMillis.toString())
        }
    }

private fun Any.toJsonPrimitive(): JsonPrimitive =
    when (this) {
        is Boolean -> JsonPrimitive(this)
        is Number -> JsonPrimitive(this)
        is String -> JsonPrimitive(this)
        else -> JsonPrimitive(toString())
    }
